import {
  type MouseEvent,
  type ComponentPropsWithoutRef,
  useState,
} from "react";
import { type BaseButtonProps } from "components/button/base";
import { Button, type ButtonProps } from "components/button";
import { Trans, useTranslation } from "react-i18next";
import { IconButton } from "components/button/icon";
import { numberFormatter } from "utils/formatters/number";
import { useCoverage, useMinContentBox } from "../../../utils";
import CheckIconAsset from "assets/images/icons/check-light.svg";
import {
  CheckIcon,
  PlusIcon,
  ExpandIcon,
  Container,
  ContentWrapper,
  CtaDisabledButton,
  CtaContainer,
  CtaPrice,
  CtaWrapper,
  Description,
  Title,
  Trigger,
  TitleCta,
  CtaDisabled,
} from "./styles";

export interface ItemProps extends ComponentPropsWithoutRef<"li"> {
  CtaButtonProps?: ButtonProps;
  coverageType: string;
  description: string;
  price?: number | false | null;
  priceSuffix?: string | null;
  title: string;
  triggerLabel?: string | null;
  TriggerProps?: BaseButtonProps;
  isDisabled?: boolean;
  ctaTextValues?: [string, string] | null;
  inlineActionInfo?: boolean;
}

export function Item({
  CtaButtonProps = {},
  coverageType,
  description,
  price = null,
  priceSuffix,
  title,
  triggerLabel = null,
  TriggerProps = {},
  ctaTextValues,
  isDisabled,
  children,
  ...props
}: ItemProps) {
  const {
    coverage,
    enable: enableCoverage,
    disable: disableCoverage,
  } = useCoverage(coverageType);

  const { onClick, ...buttonProps } = CtaButtonProps;
  const { t } = useTranslation("screens", { keyPrefix: "offer" });
  const ButtonComponent = ctaTextValues?.length ? Button : IconButton;
  const [expanded, setExpanded] = useState(false);
  const titleRef = useMinContentBox();

  function handleCtaClick(e: MouseEvent<HTMLButtonElement>) {
    if (typeof onClick === "function") {
      if (props.inlineActionInfo && !coverage?.isSelected) {
        setExpanded(true);
      }
      onClick(e);
    } else if (coverage?.isSelected) {
      disableCoverage();
    } else {
      enableCoverage();
    }
  }

  return (
    <Container {...props}>
      <ContentWrapper
        elevation={0}
        expanded={expanded && !isDisabled}
        disableGutters
      >
        <TitleCta>
          <Title
            disabled={isDisabled}
            expandIcon={<ExpandIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            component="h4"
            onClick={() => {
              setExpanded((p) => !p);
            }}
          >
            <span ref={titleRef}>{title}</span>

            {!isDisabled && price !== null && price !== false && (
              <CtaPrice>
                {numberFormatter(price, { style: "currency" })}
                {priceSuffix && <span className="suffix">{priceSuffix}</span>}
              </CtaPrice>
            )}

            {isDisabled && (
              <CtaDisabled>{t("addOns.notAvailable")}</CtaDisabled>
            )}
          </Title>
          {price !== null ? (
            <CtaWrapper>
              <CtaContainer>
                {price === false ? (
                  <CtaDisabledButton disabled variant="outlined">
                    {t("addOns.addCta")}
                  </CtaDisabledButton>
                ) : (
                  <ButtonComponent
                    variant={coverage?.isSelected ? "standard" : "outlined"}
                    title={t(
                      coverage?.isSelected
                        ? "addOns.removeCta"
                        : "addOns.addCta",
                    )}
                    icon={coverage?.isSelected ? <CheckIcon /> : <PlusIcon />}
                    onClick={handleCtaClick}
                    disabled={isDisabled}
                    StartIcon={
                      ctaTextValues?.length &&
                      coverage?.isSelected &&
                      CheckIconAsset
                    }
                    {...buttonProps}
                  >
                    {ctaTextValues?.length
                      ? coverage?.isSelected
                        ? ctaTextValues[0]
                        : ctaTextValues[1]
                      : ""}
                  </ButtonComponent>
                )}
              </CtaContainer>
            </CtaWrapper>
          ) : (
            ctaTextValues?.length && (
              <CtaWrapper>
                <CtaContainer>
                  <Button StartIcon={CheckIconAsset}>
                    {t("printLayout.addOns.itemDescription")}
                  </Button>
                </CtaContainer>
              </CtaWrapper>
            )
          )}
        </TitleCta>

        <Description>
          <Trans>{description}</Trans>

          {triggerLabel !== null && (
            <Trigger {...TriggerProps}>{triggerLabel}</Trigger>
          )}
        </Description>
        {children}
      </ContentWrapper>
    </Container>
  );
}
